import { moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import {
  takeUntil,
  distinctUntilChanged,
  debounceTime,
  switchMap,
  filter,
  tap,
  finalize,
} from 'rxjs/operators';
import {
  ISearchArticleResult,
  SearchArticleResult,
} from '../../interfaces/i-search-article-result';
import { Observable, EMPTY, Subject } from 'rxjs';
import { ArticleStatus } from 'src/app/content-management/shared/enums/article-status';
import { SearchArticleRequest } from 'src/app/core/models/i-search-article-request';
import { ContentService } from 'src/app/content-management/shared/services/content.service';
import { Chapter, IChapter } from 'src/app/core/models/i-chapter';
import { ISearchContentResponse } from 'src/app/core/models/i-search-content-response';
import { IDashboardContent } from 'src/app/core/models/i-dashboard-content';
import { ContentTypeRouterPaths } from 'src/app/shared/enums/content-type-router-paths';
import { ContentType } from 'src/app/content-management/shared/enums/content-type';

@Component({
  selector: 'app-chapters',
  templateUrl: './chapters.component.html',
  styleUrls: ['./chapters.component.scss'],
})
export class ChaptersComponent implements OnInit, OnDestroy {
  @Input() chapterControl: FormArray;
  @Input() editions: string[];
  @Input() isDisabled: boolean;
  chapterSearchResults: IDashboardContent[] = [];
  chapterDisplayList: ISearchArticleResult[] = [];
  searchBarControl: FormControl = new FormControl('');
  isSearchingChapter: boolean = false;

  DEFAULT_PAGE_NUMBER: number = 1;
  destroy$ = new Subject<boolean>();
  pageNumber: number = this.DEFAULT_PAGE_NUMBER;
  onlySelectedEditionsControl: FormControl = new FormControl(true);
  hasMoreResults: boolean;
  isLoadingMore: boolean;

  constructor(private contentService: ContentService) {}
  ngOnInit(): void {
    this.searchBarControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(500),
        filter((searchTerm) => !!searchTerm.trim()),
        tap(() => {
          this.isSearchingChapter = true;
          this.pageNumber = this.DEFAULT_PAGE_NUMBER;
        }),
        switchMap((_) => this.searchArticles())
      )
      .subscribe((searchResults: ISearchContentResponse) => {
        this.isSearchingChapter = false;
        this.chapterSearchResults = searchResults.contentResults;
        this.chapterDisplayList = searchResults.contentResults.map(
          (p) => new SearchArticleResult(p.contentId, p.title, p.publishedDate)
        );
      });
  }

  drop(previousIndex: number, currentIndex: number) {
    this.chapterControl.markAsDirty();
    moveItemInArray(this.chapterControl.value, previousIndex, currentIndex);
    this.chapterControl.value.forEach((ch: IChapter, index: number) => {
      ch.order = index + 1;
    });
  }

  clearSearch() {
    this.chapterDisplayList = [];
    this.searchBarControl.setValue('');
  }
  onRemove(chapter: IChapter) {
    this.chapterControl.removeAt(
      this.chapterControl.value.findIndex(
        (ch) => ch.contentId === chapter.contentId
      )
    );
    this.chapterControl.value.forEach((ch: IChapter, index: number) => {
      ch.order = index + 1;
    });
  }
  loadMore() {
    this.pageNumber++;
    this.isLoadingMore = true;
    this.searchArticles()
      .pipe(finalize(() => (this.isLoadingMore = false)))
      .subscribe((searchResults: ISearchContentResponse) => {
        this.chapterSearchResults.push(...searchResults.contentResults);
        this.chapterDisplayList.push(
          ...searchResults.contentResults.map(
            (p) =>
              new SearchArticleResult(p.contentId, p.title, p.publishedDate)
          )
        );
      });
  }
  handleArticleSelected(content: ISearchArticleResult) {
    let article = this.chapterSearchResults.find(
      (p) => p.contentId === content.id
    );

    let chapter = new Chapter(
      article.contentId,
      article.surtitlesDetailed && article.surtitlesDetailed.length > 0
        ? article.surtitlesDetailed[0].name
        : '',
      article.surtitlesDetailed,
      article.title,
      article.contentType
    );
    this.addChapter(chapter);
  }
  goToContent(content: IChapter) {
    return (
      './content/' +
      ContentTypeRouterPaths[content.contentType.toString().toLowerCase()] +
      '/' +
      content.contentId
    );
  }
  private searchArticles(): Observable<ISearchContentResponse> {
    const searchTerm = this.searchBarControl.value;
    if (!searchTerm) return EMPTY;
    if (searchTerm) {
      const request = this.buildGetRequest(searchTerm);
      return this.contentService.getFiltered$(request).pipe(
        tap((searchResponse: ISearchContentResponse) => {
          if (
            searchResponse.totalMatches <=
            request.pageNumber * request.pageSize
          ) {
            this.hasMoreResults = false;
          } else {
            this.hasMoreResults = true;
          }
        })
      );
    }
  }

  private buildGetRequest(searchValue: string): SearchArticleRequest {
    const request = new SearchArticleRequest();
    request.pageNumber = this.pageNumber;
    request.pageSize = 10;
    request.searchText = searchValue;
    // Only return published articles so we don't link unavailable content
    request.articleStatuses = [ArticleStatus[ArticleStatus.Published]];

    // Only allow adding articles with the following content types
    request.contentTypeFilter = [
      ContentType.Article,
      ContentType.Gallery,
      ContentType.DigitalPublication,
      ContentType.SingleSponsor,
      ContentType.Page,
    ];

    if (this.onlySelectedEditionsControl.value) {
      request.deliveryTargetCodeFilter = this.editions;
    }

    return request;
  }
  private addChapter(content: IChapter) {
    if (
      this.chapterControl.value.filter((x) => x.contentId == content.contentId)
        .length === 0
    ) {
      content = { ...content, order: this.chapterControl.value.length + 1 };
      this.chapterControl.push(new FormControl(content));
    }
  }
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
